import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { saveAs } from 'file-saver';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import type { ApiGatewayRequestOptions, HttpClientRequestOptions } from './api-gateway.models';

export abstract class ApiGatewayGlobalOptions {
  abstract version: string;
  // NOTE: kept as a function to allow lazy evaluation, the correct locale might not be known at construction time
  abstract getLocale: () => string;
}

@Injectable({ providedIn: 'root' })
export class ApiGateway {
  private http = inject(HttpClient);
  private globalOptions = inject(ApiGatewayGlobalOptions, { optional: true });

  private request<T = any>(requestMethod: string, url: string, options: ApiGatewayRequestOptions): Observable<T> {
    url = this.getFullUrl(url);
    const meta = options && options.meta;
    const httpClientRequestOptions = this.getRequestOptions(options);

    const request = this.http
      .request(requestMethod, url, httpClientRequestOptions)
      .pipe(map((response) => (meta ? response : this.mapData(response))));

    return request;
  }

  get<T = any>(url: string, options: ApiGatewayRequestOptions = {}): Observable<T> {
    return this.request<T>('GET', url, options);
  }

  post<T = any>(url: string, body: string | object, options: ApiGatewayRequestOptions = {}): Observable<T> {
    options.body = body;

    return this.request<T>('POST', url, options);
  }

  put<T = any>(url: string, body: string | object, options: ApiGatewayRequestOptions = {}): Observable<T> {
    options.body = body;

    return this.request<T>('PUT', url, options);
  }

  patch<T = any>(url: string, body: string | object, options: ApiGatewayRequestOptions = {}): Observable<T> {
    options.body = body;

    return this.request<T>('PATCH', url, options);
  }

  delete<T = any>(url: string, options?: ApiGatewayRequestOptions, body?: string | object): Observable<T> {
    options = options || {};
    options.body = body;

    return this.request<T>('DELETE', url, options);
  }

  /**
   * Download a file via the api
   */
  download(url: string, fileName?: string, requestOptions: ApiGatewayRequestOptions = {}) {
    return this.#requestFile(url, requestOptions).pipe(
      map((response) => {
        fileName ??= this.#parseFileName(response.headers.get('Content-Disposition'));

        saveAs(this.#toBlob(response), fileName);

        return response;
      }),
    );
  }

  /**
   * Fetch a file via the api as a Blob, to render in the app rather than save to disk
   */
  getBlob(url: string, requestOptions: ApiGatewayRequestOptions = {}): Observable<Blob> {
    return this.#requestFile(url, requestOptions).pipe(map((response) => this.#toBlob(response)));
  }

  #requestFile(url: string, requestOptions: ApiGatewayRequestOptions = {}) {
    const options = this.getRequestOptions(requestOptions);
    options['responseType'] = 'blob';
    options['observe'] = 'response';

    return this.http.request('GET', this.getFullUrl(url), options);
  }

  #toBlob(response: { body: Blob; headers: HttpHeaders }): Blob {
    const body = response.body;
    const contentType = response.headers.get('Content-Type');

    // Re-wrapping on a null header would set the type to the string "null".
    return contentType && contentType !== body.type ? new Blob([body], { type: contentType }) : body;
  }

  #parseFileName(contentDisposition: string | null): string | undefined {
    if (contentDisposition === null) {
      return undefined;
    }

    // Semicolons to ampersands so the header parses as URL params.
    return (
      new URLSearchParams(contentDisposition.replaceAll(/; +/g, '&').replaceAll('"', '')).get('filename') ?? undefined
    );
  }

  private getRequestOptions(options: ApiGatewayRequestOptions = {}): HttpClientRequestOptions {
    let headers = new HttpHeaders();

    headers = headers.append('X-App-Type', 'Web App');
    if (this.globalOptions?.version) {
      headers = headers.append('X-App-Version', this.globalOptions.version);
    }
    if (this.globalOptions?.getLocale) {
      headers = headers.append('X-App-Locale', this.globalOptions.getLocale());
    }

    if (options.headers?.ignoreMetaMessage) {
      headers = headers.append('ignore-meta-message', options.headers.ignoreMetaMessage.toString());
    }

    if (!headers.has('Accept')) {
      headers = headers.append('Accept', 'application/json');
    }

    // Set reportProgress to false because errors that return html were incorrectly returned as ProgressEvent
    if (options.reportProgress === undefined) {
      options = { ...options, reportProgress: false };
    }

    return { ...options, headers };
  }

  getFullUrl(url: string): string {
    if (url.indexOf('http') !== -1) {
      return url;
    }

    return '/api/' + url;
  }

  private mapData(jsonResponse: object) {
    if (jsonResponse && 'data' in jsonResponse && jsonResponse.data !== undefined) {
      return jsonResponse.data;
    }

    return jsonResponse;
  }
}
